/**
 * Profile V2 API Client
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';
/**
 * Profile types (canonical home — relocated from the former root lib/api/profile.ts)
 */
export interface PrivateProfile {
  id: string;
  display_name?: string | null;
  actual_name?: string | null;
  bio?: string | null;
  profile_picture_url?: string | null;
  role?: string;
  created_at?: string;
}

export interface UpdateProfileRequest {
  display_name?: string;
  actual_name?: string;
  bio?: string;
  profile_picture_url?: string;
}

interface ValidateDisplayNameResponse {
  available: boolean;
  message: string;
}

/**
 * Get the authenticated user's profile
 */
const getProfile = async (): Promise<Result<PrivateProfile, ApiError>> => {
  try {
    const response = await apiClient.get<PrivateProfile>('/v2/me/profile');

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch profile',
    });
  }
};

/**
 * Validate display name availability
 */
const validateDisplayName = async (
  displayName: string
): Promise<Result<ValidateDisplayNameResponse, ApiError>> => {
  try {
    const response = await apiClient.post<ValidateDisplayNameResponse>(
      '/v2/me/profile/display-name/validate',
      { display_name: displayName }
    );

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to validate display name',
    });
  }
};

/**
 * Profile API client
 */
export const profileApi = {
  getProfile,
  validateDisplayName,
};
